import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  SparklesIcon, 
  ChartBarIcon, 
  LightBulbIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';

const NewsAIAnalysis = ({ newsItem, user, portfolioData, visions = [] }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);

  const analyzeNews = async () => {
    if (analysis) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post('/api/news/ai-analysis', {
        news_item: {
          title: newsItem.title,
          content: newsItem.content || newsItem.description,
          source: newsItem.source,
          published_at: newsItem.published_at,
          url: newsItem.url
        },
        user_id: user?.user_id,
        portfolio_data: portfolioData,
        visions: visions.map(v => ({
          vision_id: v.vision_id,
          title: v.title,
          target_amount: v.target_amount,
          current_amount: v.current_amount,
          target_date: v.target_date
        }))
      });

      if (response.data.success) {
        setAnalysis(response.data.analysis);
      } else {
        setError(response.data.error || 'Failed to analyze news');
      }
    } catch (err) {
      setError('Failed to analyze news. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getImpactColor = (impact) => {
    switch (impact?.toLowerCase()) {
      case 'positive': return 'text-positive';
      case 'negative': return 'text-negative';
      case 'neutral': return 'text-warning';
      default: return 'text-secondary';
    }
  };

  const getImpactIcon = (impact) => {
    switch (impact?.toLowerCase()) {
      case 'positive': return ArrowTrendingUpIcon;
      case 'negative': return ArrowTrendingDownIcon;
      case 'neutral': return MinusIcon;
      default: return QuestionMarkCircleIcon;
    }
  };

  const ImpactIcon = analysis ? getImpactIcon(analysis.portfolio_impact) : null;

  return (
    <div className="mt-4 border-t border-hairline pt-4">
      <button
        onClick={() => {
          if (!analysis && !loading) {
            analyzeNews();
          }
          setExpanded(!expanded);
        }}
        className="w-full flex items-center justify-between p-3 bg-pine-tint border border-hairline rounded-lg hover:bg-pine-tint/80 transition-colors duration-300"
      >
        <div className="flex items-center space-x-3">
          <SparklesIcon className="w-5 h-5 text-pine" />
          <span className="text-primary font-medium">
            {loading ? 'Analyzing with AI...' : analysis ? 'AI Analysis' : 'Get AI Analysis'}
          </span>
          {analysis && ImpactIcon && (
            <span className={`text-sm flex items-center gap-1 ${getImpactColor(analysis.portfolio_impact)}`}>
              <ImpactIcon className="w-4 h-4" />
              {analysis.portfolio_impact}
            </span>
          )}
        </div>
        {expanded ? (
          <ChevronUpIcon className="w-5 h-5 text-secondary" />
        ) : (
          <ChevronDownIcon className="w-5 h-5 text-secondary" />
        )}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 space-y-4"
          >
            {loading && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-hairline border-t-pine"></div>
                <span className="ml-3 text-secondary">AI is analyzing this news...</span>
              </div>
            )}

            {error && (
              <div className="flex items-center space-x-2 p-3 border border-danger/40 bg-pine-tint rounded-lg">
                <ExclamationTriangleIcon className="w-5 h-5 text-danger" />
                <span className="text-danger">{error}</span>
              </div>
            )}

            {analysis && (
              <div className="space-y-4">
                <div className="surface-panel p-4">
                  <h4 className="text-primary font-semibold mb-2 flex items-center">
                    <LightBulbIcon className="w-4 h-4 mr-2 text-pine" />
                    Summary
                  </h4>
                  <p className="text-secondary text-sm leading-relaxed">
                    {analysis.summary}
                  </p>
                </div>

                <div className="surface-panel p-4">
                  <h4 className="text-primary font-semibold mb-2 flex items-center">
                    <ChartBarIcon className="w-4 h-4 mr-2 text-pine" />
                    Portfolio Impact
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      {ImpactIcon && (
                        <span className={`text-sm font-medium flex items-center gap-1 ${getImpactColor(analysis.portfolio_impact)}`}>
                          <ImpactIcon className="w-4 h-4" />
                          {analysis.portfolio_impact} Impact
                        </span>
                      )}
                    </div>
                    <p className="text-secondary text-sm">
                      {analysis.portfolio_impact_explanation}
                    </p>
                    {analysis.affected_sectors && analysis.affected_sectors.length > 0 && (
                      <div className="mt-2">
                        <span className="text-xs text-faint">Affected Sectors:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {analysis.affected_sectors.map((sector, index) => (
                            <span key={index} className="topic-tag text-xs">
                              {sector}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {analysis.vision_recommendations && analysis.vision_recommendations.length > 0 && (
                  <div className="surface-panel p-4">
                    <h4 className="text-primary font-semibold mb-2 flex items-center">
                      <SparklesIcon className="w-4 h-4 mr-2 text-pine" />
                      Vision Adjustments
                    </h4>
                    <div className="space-y-3">
                      {analysis.vision_recommendations.map((rec, index) => (
                        <div key={index} className="border-l-2 border-pine pl-3">
                          <div className="text-sm font-medium text-primary">
                            {rec.vision_title}
                          </div>
                          <div className="text-xs text-secondary mt-1">
                            {rec.recommendation}
                          </div>
                          {rec.action_items && rec.action_items.length > 0 && (
                            <ul className="mt-2 space-y-1">
                              {rec.action_items.map((item, itemIndex) => (
                                <li key={itemIndex} className="text-xs text-secondary flex items-start">
                                  <span className="text-pine mr-2">•</span>
                                  {item}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {analysis.market_sentiment && (
                  <div className="surface-panel p-4">
                    <h4 className="text-primary font-semibold mb-2">Market Sentiment</h4>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-secondary">{analysis.market_sentiment}</span>
                      {analysis.confidence_score && (
                        <span className="text-xs text-faint figure">
                          (Confidence: {Math.round(analysis.confidence_score * 100)}%)
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NewsAIAnalysis;
